/**
 * Location Text Processor
 *
 * Single Responsibility: Filters and validates locations with coordinates
 * NOTE: Does NOT convert plain text to [Place]() format - only processes locations
 * that are already in [Place]() format in the text
 */

export class LocationTextProcessor {
  /**
   * Blacklist of common words that should not be treated as locations
   */
  private static readonly NON_LOCATION_WORDS = new Set([
    'sunrise',
    'sunset',
    'fullmoon',
    'full moon',
    'early morning',
    'late afternoon',
    'morning',
    'afternoon',
    'evening',
    'night',
    'day',
    'weekday',
    'weekend',
    'winter',
    'summer',
    'spring',
    'autumn',
    'monsoon',
    'season',
    'weather',
    'lighting',
    'atmosphere',
    'crowds',
    'composition',
    'reflections',
    'patience',
  ]);

  /**
   * Check if a place name should be treated as a location
   */
  private static isValidLocation(placeName: string): boolean {
    if (!placeName || placeName.trim().length === 0) {
      return false;
    }

    const normalizedName = placeName.toLowerCase().trim();

    // Check against blacklist
    if (this.NON_LOCATION_WORDS.has(normalizedName)) {
      return false;
    }

    // Additional check: very short single words (1-2 characters) are likely not locations
    // unless they're common abbreviations (but we'll be conservative here)
    if (normalizedName.length <= 2 && !/^[a-z]{1,2}$/i.test(normalizedName)) {
      return false;
    }

    return true;
  }

  /**
   * Check if a location has valid coordinates (marker available)
   */
  private static hasValidCoordinates(location: {
    place?: string;
    latitude?: number;
    longitude?: number;
  }): boolean {
    return (
      typeof location.latitude === 'number' &&
      typeof location.longitude === 'number' &&
      !isNaN(location.latitude) &&
      !isNaN(location.longitude) &&
      location.latitude >= -90 &&
      location.latitude <= 90 &&
      location.longitude >= -180 &&
      location.longitude <= 180
    );
  }

  /**
   * Filter locations to only include those with valid coordinates (markers available)
   * and deduplicate by place name (case-insensitive)
   *
   * NOTE: This method no longer converts plain text to [Place]() format.
   * Only locations already in [Place]() format in the text will be processed.
   */
  static getValidLocations(
    locations: Array<{
      place?: string;
      title?: string;
      latitude?: number;
      longitude?: number;
    }>
  ): Array<{
    place?: string;
    title?: string;
    latitude?: number;
    longitude?: number;
  }> {
    if (!locations || locations.length === 0) return [];

    // First, filter to only include locations with valid coordinates (markers available)
    // and valid place names
    const validLocations = locations.filter(loc => {
      const placeName =
        loc.place ||
        loc.title ||
        (loc as { place_name?: string }).place_name ||
        '';
      return (
        placeName &&
        this.isValidLocation(placeName) &&
        this.hasValidCoordinates(loc)
      );
    });

    // Deduplicate by place name (case-insensitive)
    // Keep the first occurrence of each unique place name
    const seenPlaceNames = new Set<string>();
    const uniqueLocations = validLocations.filter(location => {
      const placeName = (
        location.place ||
        location.title ||
        (location as { place_name?: string }).place_name ||
        ''
      )
        .toLowerCase()
        .trim();
      if (seenPlaceNames.has(placeName)) {
        return false;
      }
      seenPlaceNames.add(placeName);
      return true;
    });

    return uniqueLocations;
  }
}
